/*
src/data/ForegroundObjectManager.js
wgbh-skydiagram-testing
astro.unl.edu
2019-07-14
*/


import {CallbackMakerMixin} from '../utils/CallbackMakerMixin.js';

const uuidv4 = require('uuid/v4');


/*

This class manages (represents) a foreground object in the SkyDiagram component. Instances of
	this class are created by ParamsManager.

A ForegroundObjectManager is created by a ParamsManager.

Properties:
	id															- constant
	name
	system
	x, y
	offsetX, offsetY
	width
	applyNightShading
	image
	reference												- either null, or a ForegroundObjectManager instance
	shouldRestrictVisibilityByTime
	timesVisible										- each object in array has beginHour, endHour, and id properties;
																		the id is an automatically generated uuid, provided for use as a react key
	canDelete												- read-only, derived
	isUsedAsReference								- read-only, derived
	canBeUsedAsReference						- read-only, derived


Callbacks:
	ready			- called after asychronous initialization (i.e. database)
	change		- called when any of the properties have changed; the data argument will
							be an object with these properties:
								newValues 	- an object whose key/values correspond to the properties/newValues
								deltaParams - either null, or a delta parameters object suitable for passing
															to a SkyDiagram component within a foregroundObjects array
							For example, changing the name will cause the data argument to look like this:
								data = {newValues: {name: "new name"}, deltaParams: null}
							since the name property does not belong to the parameter set.

Internally, the foreground object manager also makes a callback on the parent params
	manager when params have changed, which leads to the params manager also making a change callback.
 
*/


const STORE_NAME = 'foreground-objects';


export class ForegroundObjectManager {

	static upgradeDatabase(db) {
		db.createObjectStore(STORE_NAME, {keyPath: 'id'});
	}

	constructor(workspace, paramsManager, initObj) {

		this._db = paramsManager._db;

		// Note: ParamsManager will access _id directly (read-only).

		this._workspace = workspace;
		this._paramsManager = paramsManager;

		this._needsReferenceResolution = false;

		// The reference retain count is a subset of the overall retain count. For example, retaining
		//	the object as a reference increments both counters.
		this._retainCount = 0;
		this._referenceRetainCount = 0;

		// Default settings follow (some or all will be overwritten later):

		// Name and id will be overwritten by the _init* methods.
		// _id backs the ID parameter (note case difference).
		// _name is just for the testing framework -- it is not part of the SkyDiagram params.
		this._id = '';
		this._name = '';

		// simpleParams can be copied by assignment.
		this._simpleParams = {
			system: 'horizon',
			x: 0,
			y: 0,
			offsetX: 0,
			offsetY: 0,
			width: 0.1,
			applyNightShading: true,
		};

		// _image backs the imageSrc parameter, and is either an SDImage or null.
		// The aspectRatio parameter is derived from the image.
		this._image = null;

		// _reference backs the refObjectID parameter, and is either a ForegroundObjectManager or null.
		this._reference = null;

		// These two properties back the visibility parameter.
		// _shouldRestrictVisibilityByTime determines whether the visibility.sunPosition
		//	parameter is included or not.
		// _timesVisible backs the visibility.sunPosition parameter, and is an array
		//	of {beginHour, endHour} interval objects.
		this._shouldRestrictVisibilityByTime = false;
 		this._timesVisible = [];

		if (typeof initObj === 'object') {
			if (initObj.hasOwnProperty('def')) {
				this._initWithDef(initObj.def);
			} else if (initObj.hasOwnProperty('image')) {
				this._initWithImage(initObj.image);
			} else if (initObj.hasOwnProperty('database') && initObj.hasOwnProperty('id')) {
				// The database option requires that the id be provided.
				// The database value is ignored and the paramsManager's is used.
				this._initWithDatabase(initObj.id);
			} else {
				console.warn('ForegroundObjectManager\'s constructor received an initObj, but did not find any recognized options.');
				this._init();
			}
		} else {
			this._init();
		}

		CallbackMakerMixin.apply(this);
	}

	_init() {
		this._id = uuidv4();
		this._name = 'New Object';
		this._store();
	}

	_initWithImage(image) {
		this._id = uuidv4();
		this._name = 'New Object';
		this.setImage(image, true);
		this._store();
	}

	_initWithDatabase(id) {
		this._id = id;
		let t = this._db.transaction(STORE_NAME, 'readonly');
		t.onabort = (e) => {
			console.error('The database transaction was aborted.');
		};
		let r = t.objectStore(STORE_NAME).get(this._id);
		r.onsuccess = (e) => {
			this._finishInitWithDatabase(e.target.result);
		};
		r.onerror = (e) => {
			console.error('Failed to load stored object from database. Error: '+e.target.errorCode);
		};
	}

	_finishInitWithDatabase(dbObj) {
		if (typeof dbObj === 'object') {
			this._initWithDef(dbObj);
		} else {
			console.error('A ForegroundObject was referenced in the params database, but could not be found in the foreground objects database.');
			this._name = 'Error - see console.';
		}
		this.makeCallback('ready');
	}

	_getDef() {
		let def = {};

		def.id = this._id;
		def.name = this._name;

		for (const key in this._simpleParams) {
			def[key] = this._simpleParams[key];
		}

		def.shouldRestrictVisibilityByTime = this._shouldRestrictVisibilityByTime;

		def.timesVisible = this._copyTimesVisibleArray(this._timesVisible);

		if (this._image !== null) {
			def.imageId = this._image.getId();
		}

		if (this._reference !== null) {
			def.referenceId = this._reference.getId();
		}
		
		return def;
	}

	_initWithDef(def) {

		// id and name are expected to be assigned in _init* methods.
		this._id 			=	def.hasOwnProperty('id') 		? def.id 		: uuidv4();
		this._name		=	def.hasOwnProperty('name')	?	def.name	: 'Object';

		for (const key in this._simpleParams) {
			if (def.hasOwnProperty(key)) {
				this._simpleParams[key] = def[key];
			}
		}

		if (def.hasOwnProperty('shouldRestrictVisibilityByTime')) {
			this._shouldRestrictVisibilityByTime = Boolean(def.shouldRestrictVisibilityByTime);
		}

		if (def.hasOwnProperty('timesVisible')) {
			this._timesVisible = this._copyTimesVisibleArray(def.timesVisible);
		}

		// The image and reference properties are stored in the def object as ids. These
		//	need to be resolved into actual references to SDImage and ForegroundObjectManager
		//	instances.
		// To ensure that referenced objects exist before trying to resolve them initialization
		//	is performed in two steps: create the instances, then invoke resolveReferences on them.

		this._image = null;
		this._reference = null;

		this._initImageId 			= def.hasOwnProperty('imageId') 			?	def.imageId				: null;
		this._initReferenceId		= def.hasOwnProperty('referenceId')		? def.referenceId		:	null;
		
		this._needsReferenceResolution = true;

		// store is deferred to resolveReferences
	}

	_destroy() {
		// Called by ParamsManager when it is about to delete this foreground object.
		// The purpose of this method is to release any retained resources, and
		//	remove all callback registrations.
		if (!this.getCanDelete()) {
			console.error('Failed to destroy a foreground object.');
			return;
		}
		this.setImage(null, true);
		this.setReference(null, true);
		this.unregisterAllCallbacks();
	}

	getCanDelete() {
		return !(this._retainCount > 0);
	}

	resolveReferences() {
		// Must be called after instance creation.

		if (!this._needsReferenceResolution) {
			return;
		}

		// If the lookup methods fail they return null, which is OK.
		// The lookup methods accept null as an argument.
		// Passing true as the second argument skips callbacks to the params manager.
		let imageBank = this._workspace.getForegroundObjectsImageBank();
		this.setImage(imageBank.getImageWithId(this._initImageId), true);
		this.setReference(this._paramsManager.getForegroundObjectWithId(this._initReferenceId), true);

		this._needsReferenceResolution = false;

		this._store();
	}


	/*
	**	Time Restricted Visibility Methods
	*/

	getShouldRestrictVisibilityByTime() {
		return this._shouldRestrictVisibilityByTime;
	}

	setShouldRestrictVisibilityByTime(arg) {
		arg = Boolean(arg);
		if (arg === this._shouldRestrictVisibilityByTime) {
			return;
		}
		this._shouldRestrictVisibilityByTime = arg;
		
		let newValues = {shouldRestrictVisibilityByTime: this._shouldRestrictVisibilityByTime};
		let deltaParams = {visibility: this.getVisibilityParam()};

		this._paramsManager._onForegroundObjectUpdate(this, deltaParams, false);
		this._makeChangeCallback({newValues: newValues, deltaParams: deltaParams});
	}

	getTimesVisible() {
		return this._copyTimesVisibleArray(this._timesVisible);
	}

	_copyTimesVisibleArray(original) {
		let copy = [];
		for (let i = 0; i < original.length; ++i) {
			let interval = original[i];
			let id;
			if (interval.hasOwnProperty('id')) {
				id = interval.id;
			} else {
				id = uuidv4();
			}
			copy[i] = {beginHour: interval.beginHour, endHour: interval.endHour, id: id};
		}
		return copy;
	}

	addNewTimesVisibleInterval(beginHour, endHour) {
		beginHour = (beginHour%24 + 24)%24;
		endHour = (endHour%24 + 24)%24;
		if (Number.isNaN(beginHour) || Number.isNaN(endHour)) {
			console.error('Attempt to add new time visibility interval failed. The values are invalid.'); 
			return;
		}
		this._timesVisible.push({beginHour: beginHour, endHour: endHour, id: uuidv4()});
		this._onTimesVisibleChanged();
	}

	deleteTimesVisibleIntervalAtIndex(index) {
		if (index < 0 || index >= this._timesVisible.length) {
			console.error('Can\'t delete time visibility interval. The index is out of range.');
			return;
		}
		this._timesVisible.splice(index, 1);
		this._onTimesVisibleChanged();
	}	

	_onTimesVisibleChanged() {
		let newValues = {timesVisible: this.getTimesVisible()};
		let deltaParams = {visibility: this.getVisibilityParam()};
		this._paramsManager._onForegroundObjectUpdate(this, deltaParams, false);
		this._makeChangeCallback({newValues: newValues, deltaParams: deltaParams});
	}

	getVisibilityParam() {
		// This method returns either an empty object (no time restrictions), or
		//	a sunPosition array of restrictions as the SkyDiagram component expects.
		// Recall: position 0.0 corresponds to the left horizon node, ascending.
		let v = {};
		if (this._shouldRestrictVisibilityByTime) {
			let s = [];
			for (let i = 0; i < this._timesVisible.length; ++i) {
				let interval = this._timesVisible[i];
				s.push({
					begin: (interval.beginHour - 6)/24,
					end: (interval.endHour - 6)/24,
				});
			}
			v.sunPosition = s;
		}
		return v;
	}


	/*
	**	Name Property Methods
	*/

	getName() {
		return this._name;
	}

	setName(name) {
		if (name === this._name) {
			return;
		}
		this._name = name;
		// name is not a parameter -- no need to inform ParamsManager.
		this._makeChangeCallback({newValues: {name: this._name}, deltaParams: null});
	}


	/*
	**	General Params Methods
	**
	**	Each object in a foregroundObjects parameter array has ID and params (optional) properties.
	**	The parent ParamsManager is responsible for assembling those objects using these methods. 
	*/
	
	getId() {
		return this._id;
	}

	getParams(subset) {
		// Like ParamsManager's getParams method, this method will return either a full
		//	params object or a partial one using the provided subset object.

		if (typeof subset === 'object') {
			// Return a subset of params.

			for (const key in this._simpleParams) {
				if (subset.hasOwnProperty(key)) {
					subset[key] = this._simpleParams[key];
				}
			}

			if (subset.hasOwnProperty('imageSrc')) {
				subset.imageSrc = this.getImageSrcParam();
			}

			if (subset.hasOwnProperty('aspectRatio')) {
				subset.aspectRatio = this.getAspectRatioParam();
			}

			if (subset.hasOwnProperty('visibility')) {
				subset.visibility = this.getVisibilityParam();
			}

			if (subset.hasOwnProperty('refObjectID')) {
				subset.refObjectID = this.getRefObjectIDParam();
			}

			return subset;

		} else {
			// Return a complete params object.

			let p = Object.assign({}, this._simpleParams);

			p.imageSrc = this.getImageSrcParam();
			p.aspectRatio = this.getAspectRatioParam();
			p.visibility = this.getVisibilityParam();
			p.refObjectID = this.getRefObjectIDParam();

			// TODO: remove
			// imageName is not a real SkyDiagram parameter. Thist is just a hack to make copying
			//	settings to the clipboard more useful.
			if (this._image !== null) {
				p.imageName = this._image.getName();
			}

			return p;
		}
	}

	setParams(arg) {
		// This method allows setting one or more params at a time using
		//	a delta object as the argument.

		let needsCallback = false;
		let deltaParams = {};

		for (const key in this._simpleParams) {
			if (arg.hasOwnProperty(key)) {
				this._simpleParams[key] = arg[key];
				deltaParams[key] = arg[key];
				needsCallback = true;
			}
		}

		if (arg.hasOwnProperty('imageSrc')) {
			console.error('Setting imageSrc in setParams is not supported.');
		}

		if (arg.hasOwnProperty('refObjectID')) {
			console.error('Setting refObjectID in setParams is not supported.');
		}

		if (arg.hasOwnProperty('visibility')) {
			console.error('Setting visibility in setParams is not supported.');
		}

		if (arg.hasOwnProperty('aspectRatio')) {
			console.error('Setting aspectRatio in setParams is not supported.');
		}

		if (needsCallback) {
			this._paramsManager._onForegroundObjectUpdate(this, deltaParams, false);
			this._makeChangeCallback({newValues: deltaParams, deltaParams: deltaParams});
		}
	}


	/*
	**	Object's Image Methods
	*/

	getImage() {
		return this._image;
	}

	setImage(image, noCallback) {

		if (image === this._image) {
			return;
		}

		if (this._image !== null) {
			this._image.unregisterCallback('change', this._imageChangeCallback);
			this._image.release();
		}

		this._image = image;

		if (this._image !== null) {
			this._image.retain();
			this._image.registerCallback('change', this._imageChangeCallback);
		}

		if (!noCallback) {
			let newValues = {image: this._image};
			let deltaParams = {imageSrc: this.getImageSrcParam(), aspectRatio: this.getAspectRatioParam()};
			this._paramsManager._onForegroundObjectUpdate(this, deltaParams, false);
			this._makeChangeCallback({newValues: newValues, deltaParams: deltaParams});
		}
	}

	getImageSrcParam() {
		if (this._image !== null) {
			return this._image.getSrc();
		} else {
			return '';
		}
	}

	getAspectRatioParam() {
		if (this._image !== null) {
			return this._image.getAspectRatio();
		} else {
			return 1;
		}
	}

	_imageChangeCallback(type, caller, data) {
		if (typeof data === 'object' && data.hasOwnProperty('aspectRatio')) {
			let deltaParams = {aspectRatio: this.getAspectRatioParam()};
			this._paramsManager._onForegroundObjectUpdate(this, deltaParams, false);
		}
	}


	/*
	**	Reference Object Methods
	*/

	getReference() {
		return this._reference;
	}

	setReference(reference, noCallback) {

		if (reference === this._reference) {
			return;
		}

		if (this._reference !== null) {
			this._reference.releaseAsReference();
		}

		this._reference = reference;

		if (this._reference !== null) {
			this._reference.retainAsReference();
		}

		if (!noCallback) {
			let newValues = {reference: this._reference};
			let deltaParams = {refObjectID: this.getRefObjectIDParam()};
			this._paramsManager._onForegroundObjectUpdate(this, deltaParams, false);
			this._makeChangeCallback({newValues: newValues, deltaParams: deltaParams});
		}
	}

	getRefObjectIDParam() {
		if (this._reference !== null) {
			return this._reference.getId();
		} else {
			return '';
		}
	}


	/*
	**	Lifecycle Management
	*/

	getCanBeUsedAsReference() {
		return (this._simpleParams.system === 'horizon');	
	}

	getIsUsedAsReference() {
		return (this._referenceRetainCount > 0);
	}

	retainAsReference() {
		this._referenceRetainCount += 1;
		if (this._referenceRetainCount === 1) {
			// isUsedAsReference is now true.
			this._makeChangeCallback({newValues: {isUsedAsReference: true}, deltaParams: null});
		}
		this.retain();
	}

	releaseAsReference() {
		this._referenceRetainCount -= 1;
		if (this._referenceRetainCount < 0) {
			console.error('A foreground object\'s reference retain count is less than zero.');
		}
		if (this._referenceRetainCount === 0) {
			// isUsedAsReference is now false.
			this._makeChangeCallback({newValues: {isUsedAsReference: false}, deltaParams: null});
		}
		this.release();
	}

	retain() {
		let couldDelete = this.getCanDelete();
		this._retainCount += 1;
		if (couldDelete && this._retainCount === 1) {
			// The object is no longer deletable after being retained.
			this._makeChangeCallback({newValues: {canDelete: false}, deltaParams: null});
		}
	}

	release() {
		this._retainCount -= 1;
		if (this._retainCount < 0) {
			console.error('A foreground object\'s retain count is less than zero.');
		}
		if (this._retainCount === 0 && this.getCanDelete()) {
			// The object has been completely released and is now deletable.
			this._makeChangeCallback({newValues: {canDelete: true}, deltaParams: null});
		}
	}


	/*
	**	Change
	*/

	_makeChangeCallback(data) {
		this._store();
		this.makeCallback('change', data);
	}

	_store() {
		if (this._db === null) {
			return;
		}
		let dbObj = this._getDef();
		let t = this._db.transaction(STORE_NAME, 'readwrite');
		t.onabort = (e) => {
			console.error('The database transaction was aborted. Local storage space may not be available.');
		};
		let r = t.objectStore(STORE_NAME).put(dbObj);
		r.onerror = (e) => {
			console.error('Failed to store object in database. Error: '+e.target.errorCode);
		};	
	}
}


